import { ProductSummary } from '@fdc/__generated__/graphql';
import {
  getAddToCartTypeFromUnit,
  getInitialVariantKey,
} from '@fdc/components/ui/product-tile/add-to-cart-helpers';
import { useCart } from '@fdc/hooks/nz';
import { useAddToCartOrStepper } from '@fdc/hooks/nz/mutations/useAddToCartOrStepper';
import { useProductTileUnitToggle } from '@fdc/hooks/useProductTileUnitToggle';
import { mapProductSummary } from '@fdc/utils/product-summary.mapper';
import { createSelectItemClickHandler } from '@fdc/utils/select-item-analytics';
import {
  getUomFromVariantKey,
  toTileVariants,
} from '@fdc/utils/variant-summary-utils';

import type { ProductCarouselAnalyticsProps } from './product-carousel-model';
import { ProductCarouselTile } from './product-carousel-tile';

export interface ProductCarouselCartWrapperProps {
  product: ProductSummary;
  index: number;
  itemListId?: string;
  itemListName?: string;
  displayNarrowLayout?: boolean;
  analyticsProps?: ProductCarouselAnalyticsProps;
  reserveHeroPriceSpace?: boolean;
  reserveProductTagSpace?: boolean;
}

export const ProductCarouselCartTileWrapper: React.FC<
  ProductCarouselCartWrapperProps
> = ({
  product,
  index,
  itemListId,
  itemListName,
  analyticsProps,
  displayNarrowLayout,
  reserveHeroPriceSpace,
  reserveProductTagSpace,
}) => {
  const { customerCart } = useCart();
  const { handleAddToCartClick, handleStepperChange } = useAddToCartOrStepper({
    includeLineItems: false,
  });

  const cartLineItem = customerCart?.lineItems?.find(
    (li) => li.sku === product.sku
  );

  const variants = product.variants ?? [];
  const tileVariants = toTileVariants(variants);

  const cartUom = getUomFromVariantKey(
    variants,
    cartLineItem?.productVariantSku
  );
  const initialVariantKey = getInitialVariantKey(tileVariants, {
    cartSelectedUom: cartUom,
    cartQuantity: cartLineItem?.quantity ?? undefined,
  });
  const { selectedType: selectedVariantKey, handleSelectedUnitTypeChange } =
    useProductTileUnitToggle<string>({
      initialType: initialVariantKey ?? '',
      quantity: cartLineItem?.quantity ?? undefined,
    });

  const selectedVariant = variants.find(
    (v) => v.variantKey === selectedVariantKey
  );
  const selectedType = getAddToCartTypeFromUnit(
    selectedVariant?.purchaseUnit?.unit
  );

  const resolvedAnalyticsOptions =
    analyticsProps?.analyticsOptions ??
    analyticsProps?.getAnalyticsOptions?.(index);

  const tileProps = mapProductSummary(
    product,
    cartLineItem,
    handleAddToCartClick,
    handleStepperChange,
    itemListId
      ? {
          itemListId,
          itemListName: itemListName || '',
          itemListIndex: index,
          analyticsOptions: resolvedAnalyticsOptions,
        }
      : undefined,
    selectedType
  );

  const onProductClick = itemListId
    ? createSelectItemClickHandler(
        product,
        index,
        itemListId,
        itemListName || '',
        resolvedAnalyticsOptions
      )
    : undefined;

  return (
    <ProductCarouselTile
      {...tileProps}
      selectedVariantKey={selectedVariantKey}
      onSelectedVariantKeyChange={handleSelectedUnitTypeChange}
      onProductClick={onProductClick}
      displayNarrowLayout={displayNarrowLayout}
      reserveHeroPriceSpace={reserveHeroPriceSpace}
      reserveProductTagSpace={reserveProductTagSpace}
    />
  );
};
